import { TextGeometry, TextGeometryParameters } from 'three/addons/geometries/TextGeometry.js';
import * as THREE from 'three';
import textHelvetiker from 'three/examples/fonts/helvetiker_bold.typeface.json?url';
import { Font, FontLoader } from 'three/examples/jsm/Addons.js';
import gsap from 'gsap';
import { OptionTitle } from './menu-datas';

export type OptionTitleData = {
  title: OptionTitle;
  position: THREE.Vector2;
};

export class MenuOptionsTitleView {
  private textMeshes: Map<string, THREE.Object3D> = new Map();
  private lastDisplayedTextMesh?: THREE.Object3D;
  private defaultOptionTitle: OptionTitle;

  constructor(group: THREE.Group, options: OptionTitleData[], defaultOptionTitle: OptionTitle) {
    this.defaultOptionTitle = defaultOptionTitle;

    const loader = new FontLoader();
    const spawnTexts = this.spawnTexts.bind(this);
    loader.load(textHelvetiker, function (font: Font) {
      spawnTexts(options, font, group);
    });
  }

  private spawnTexts(options: OptionTitleData[], font: Font, group: THREE.Group) {
    const param: TextGeometryParameters = {
      font: font,
      size: 0.45,
      depth: 0.05,
      curveSegments: 12,
      bevelEnabled: true,
      bevelThickness: 0.1,
      bevelSize: 0.1,
      bevelOffset: 0,
      bevelSegments: 2,
    };

    const materials = [
      new THREE.MeshBasicMaterial({ color: 0xffffff }),
      new THREE.MeshBasicMaterial({ color: 0x4182b0 }),
    ];

    for (const option of options) {
      const geometry = new TextGeometry(option.title, param);
      geometry.center();

      const textMesh = new THREE.Mesh(geometry, materials);
      textMesh.scale.set(0, 1, 0); // Start with scale 0 to hide it initially
      group.add(textMesh);

      textMesh.position.set(option.position.x, option.position.y, 2.3);
      this.textMeshes.set(option.title, textMesh);
    }
  }

  public moveText(title: OptionTitle, position: THREE.Vector2) {
    const textMesh = this.textMeshes.get(title);
    if (!textMesh) return;
    gsap.killTweensOf(textMesh.position);
    gsap.to(textMesh.position, { x: position.x, y: position.y, duration: 0.15 });
  }

  public resetDefaultOption() {
    if (this.lastDisplayedTextMesh) this.lastDisplayedTextMesh.scale.set(0, 1, 0);
    this.lastDisplayedTextMesh = this.textMeshes.get(this.defaultOptionTitle);
    this.lastDisplayedTextMesh!.scale.set(1, 1, 1);
  }

  public selectOption(title: string) {
    if (this.lastDisplayedTextMesh) {
      gsap.killTweensOf(this.lastDisplayedTextMesh.scale);
      gsap.to(this.lastDisplayedTextMesh.scale, { x: 0, z: 0, duration: 0.05 });
    }
    const newText = this.textMeshes.get(title)!;
    gsap.to(newText.scale, { x: 1, z: 1, duration: 0.2, ease: 'back.out' });

    this.lastDisplayedTextMesh = newText;
  }
}
